<script setup lang="ts">
  import { ref, computed } from 'vue'
  import axios from 'axios'
  import { Media, MediaCollection } from '@/types'

  const props = defineProps({
    selectionCount: {
      type: Number,
      required: true,
    },
    selectionIds: {
      type: Array<number>,
      required: true,
    },
    selectedMedias: {
      type: Array as () => Media[],
      default: () => [],
    },
    collections: {
      type: Array<MediaCollection>(),
      required: true,
    },
  })

  const emit = defineEmits(['deselect-all', 'deleted', 'moved'])

  const loading = ref<boolean>(false)
  const error = ref<string>('')
  const moveCollectionId = ref<number|null>(null)
  const showDeleteModal = ref<boolean>(false)

  const linkedMedias = computed(() => {
    return props.selectedMedias.filter(m =>
      (m.events?.length || 0) + (m.locations?.length || 0) + (m.logos?.length || 0) > 0
    )
  })

  const openDeleteModal = () => {
    showDeleteModal.value = true
  }

  const closeDeleteModal = () => {
    showDeleteModal.value = false
  }

  const bulkDelete = async () => {
    loading.value = true
    error.value = ''

    try {
      const response = await axios.delete('/api/media/bulk', { data: { ids: props.selectionIds } })
      emit('deleted', response.data.deleted)
      showDeleteModal.value = false
    } catch (e: any) {
      error.value = e.response?.data?.message || 'Löschen fehlgeschlagen'
    } finally {
      loading.value = false
    }
  }

  const bulkMove = async () => {
    if (!moveCollectionId.value) return

    loading.value = true
    error.value = ''

    try {
      await axios.post('/api/media/bulk/move', {
        ids: props.selectionIds,
        collection_id: moveCollectionId.value,
      })
      emit('moved', props.selectionIds, moveCollectionId.value)
      moveCollectionId.value = null
    } catch (e: any) {
      error.value = e.response?.data?.message || 'Verschieben fehlgeschlagen'
    } finally {
      loading.value = false
    }
  }
</script>

<template>
  <div class="card bg-base-200 p-4 ml-4 sticky top-0">
    <div class="flex items-center justify-between mb-4">
      <h3 class="font-semibold text-lg">{{ selectionCount }} ausgewählt</h3>
      <button class="btn btn-ghost btn-xs" @click="emit('deselect-all')">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 24 24"><path fill="currentColor" d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z"/></svg>
        Aufheben
      </button>
    </div>

    <div v-if="error" class="alert alert-error text-sm mb-4 py-2">
      {{ error }}
      <button class="btn btn-ghost btn-xs" @click="error = ''">✕</button>
    </div>

    <!-- Move to collection -->
    <div class="form-control mb-4">
      <label class="label">
        <span class="label-text text-sm">In Sammlung verschieben</span>
      </label>
      <div class="flex gap-2">
        <select class="select select-bordered select-sm flex-1" v-model="moveCollectionId">
          <option :value="null" disabled>Sammlung wählen…</option>
          <option v-for="col in collections" :key="col.id" :value="col.id">{{ col.name }}</option>
        </select>
        <button class="btn btn-sm btn-info" :disabled="!moveCollectionId || loading" @click="bulkMove">
          Verschieben
        </button>
      </div>
    </div>

    <div class="divider my-2 text-xs">oder</div>

    <!-- Delete -->
    <div class="form-control">
      <button
        class="btn btn-sm btn-outline btn-error w-full"
        :disabled="loading"
        @click="openDeleteModal"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 24 24"><path fill="currentColor" d="M19,4H15.5L14.5,3H9.5L8.5,4H5V6H19M6,19A2,2 0 0,0 8,21H16A2,2 0 0,0 18,19V7H6V19Z"/></svg>
        Löschen
      </button>
    </div>

    <div v-if="loading" class="flex justify-center mt-4">
      <span class="loading loading-spinner loading-sm"></span>
    </div>
  </div>

  <!-- Delete Confirmation Modal -->
  <dialog class="modal" :class="{ 'modal-open': showDeleteModal }">
    <div class="modal-box max-w-[700px]">
      <h3 class="font-bold text-lg">{{ selectionCount }} Bilder wirklich löschen?</h3>
      <p class="py-4">Folgende Bilder werden unwiderruflich gelöscht:</p>

      <ul class="list-disc ml-6 mb-4 text-sm">
        <li v-for="media in selectedMedias" :key="media.id">
          {{ media.name }} <span class="opacity-50">(ID: {{ media.id }})</span>
        </li>
      </ul>

      <div v-if="linkedMedias.length > 0" class="collapse collapse-arrow bg-base-200 mb-4">
        <input type="checkbox" checked />
        <div class="collapse-title font-medium">
          <span class="text-warning">⚠</span> {{ linkedMedias.length }} {{ linkedMedias.length === 1 ? 'Bild ist' : 'Bilder sind' }} noch mit Inhalten verknüpft:
        </div>
        <div class="collapse-content">
          <div v-for="media in linkedMedias" :key="media.id" class="mb-2">
            <p class="font-semibold text-sm">{{ media.name }}:</p>
            <ul class="list-disc ml-6 text-sm">
              <li v-for="event in media.events" :key="'e'+event.id">{{ event.title }} (Event)</li>
              <li v-for="location in media.locations" :key="'l'+location.id">{{ location.title }} (Location)</li>
              <li v-for="logo in media.logos" :key="'lo'+logo.id">{{ logo.name }} (Logo)</li>
            </ul>
          </div>
        </div>
      </div>

      <div v-if="error" class="alert alert-error text-sm mb-4 py-2">
        {{ error }}
      </div>

      <div class="modal-action">
        <button class="btn btn-error" :disabled="loading" @click="bulkDelete">
          <span v-if="loading" class="loading loading-spinner loading-sm"></span>
          <span v-else>Endgültig löschen</span>
        </button>
        <button class="btn" @click="closeDeleteModal">Abbrechen</button>
      </div>
    </div>
    <div class="modal-backdrop" @click="closeDeleteModal"></div>
  </dialog>
</template>
